import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { EmptyState } from '@/components/ui/EmptyState'
import { Spinner } from '@/components/ui/Spinner'
import { Avatar } from '@/components/ui/Avatar'
import { formatDate } from '@/lib/utils'
import {
  Plus,
  CheckCircle,
  Circle,
  Clock,
  Trash2,
  Calendar,
  ListTodo,
  FileText,
  ArrowRight,
} from 'lucide-react'
import type { ProjectTask, TaskFormData } from '@/types/project'
import { TASK_STATUS_CONFIG, TASK_PRIORITY_CONFIG } from '@/types/project'
import {
  useProjectTasks,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
} from '@/hooks/useProjects'
import { useAdmins } from '@/hooks/useAdmins'

interface TasksTabProps {
  projectId: string
}

function TaskStatusIcon({ status }: { status: ProjectTask['status'] }) {
  switch (status) {
    case 'done':
      return <CheckCircle className="w-5 h-5 text-mint-500" />
    case 'in_progress':
      return <Clock className="w-5 h-5 text-blue-500" />
    default:
      return <Circle className="w-5 h-5 text-brown-300" />
  }
}

function PriorityBadge({ priority }: { priority: ProjectTask['priority'] }) {
  const config = TASK_PRIORITY_CONFIG[priority]
  return <Badge variant={config.color} size="sm">{config.label}</Badge>
}

function getProvenanceFromNotes(notes: string | null): string | null {
  if (!notes) return null
  const match = notes.match(/^Créée depuis l'intervention:\s*(.+)$/)
  return match ? match[1] : null
}

function TaskModal({
  isOpen,
  onClose,
  projectId,
  task,
}: {
  isOpen: boolean
  onClose: () => void
  projectId: string
  task?: ProjectTask
}) {
  const [titre, setTitre] = useState(task?.titre || '')
  const [description, setDescription] = useState(task?.description || '')
  const [status, setStatus] = useState<ProjectTask['status']>(task?.status || 'todo')
  const [priority, setPriority] = useState<ProjectTask['priority']>(task?.priority || 'medium')
  const [dueDate, setDueDate] = useState(task?.due_date?.split('T')[0] || '')
  const [assignedTo, setAssignedTo] = useState(task?.assigned_to || '')
  const [startDate, setStartDate] = useState(task?.start_date?.split('T')[0] || '')
  const [endDate, setEndDate] = useState(task?.end_date?.split('T')[0] || '')
  const [notes, setNotes] = useState(task?.notes || '')
  const [progress, setProgress] = useState(task?.progress ?? 0)

  const { data: admins } = useAdmins()
  const createTask = useCreateTask()
  const updateTask = useUpdateTask()

  const provenance = getProvenanceFromNotes(task?.notes || null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!titre.trim()) return

    const data: TaskFormData = {
      titre: titre.trim(),
      description: description.trim() || '',
      status,
      priority,
      due_date: dueDate || null,
      assigned_to: assignedTo || null,
      start_date: startDate || null,
      end_date: endDate || null,
      notes: notes.trim() || null,
      progress,
    }

    if (task) {
      await updateTask.mutateAsync({ taskId: task.id, projectId, data })
    } else {
      await createTask.mutateAsync({ projectId, data })
    }

    handleReset()
  }

  const handleReset = () => {
    setTitre('')
    setDescription('')
    setStatus('todo')
    setPriority('medium')
    setDueDate('')
    setAssignedTo('')
    setStartDate('')
    setEndDate('')
    setNotes('')
    setProgress(0)
    onClose()
  }

  const isPending = createTask.isPending || updateTask.isPending

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleReset}
      title={task ? 'Detail de la tache' : 'Nouvelle tache'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Provenance badge */}
        {provenance && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
            <ArrowRight className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span className="text-sm text-blue-800">
              Provenance : {provenance}
            </span>
          </div>
        )}

        <Input
          label="Titre"
          value={titre}
          onChange={(e) => setTitre(e.target.value)}
          placeholder="Ex: Creer les maquettes"
          required
        />

        <Input
          label="Description (optionnel)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Details de la tache..."
        />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Statut
            </label>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as ProjectTask['status'])}
            >
              {Object.entries(TASK_STATUS_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.label}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Priorite
            </label>
            <Select
              value={priority}
              onChange={(e) => setPriority(e.target.value as ProjectTask['priority'])}
            >
              {Object.entries(TASK_PRIORITY_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.label}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/* Dates section */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            type="date"
            label="Date de debut"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            type="date"
            label="Date de fin"
            value={endDate || dueDate}
            onChange={(e) => {
              setEndDate(e.target.value)
              setDueDate(e.target.value)
            }}
          />
        </div>

        {/* Progress */}
        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">
            Progression : {progress}%
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={progress}
              onChange={(e) => setProgress(Number(e.target.value))}
              className="w-full h-2 bg-beige-200 rounded-lg appearance-none cursor-pointer accent-mint-500"
            />
          </div>
          <div className="w-full bg-beige-200 rounded-full h-2 mt-2">
            <div
              className="bg-mint-500 h-2 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">
            Notes / Commentaires
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes internes, commentaires..."
            rows={3}
            className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">
            Assigne a (optionnel)
          </label>
          <Select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
          >
            <option value="">Non assigne</option>
            {admins?.map((admin) => (
              <option key={admin.id} value={admin.id}>
                {admin.full_name}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={handleReset}>
            Annuler
          </Button>
          <Button type="submit" disabled={!titre.trim() || isPending}>
            {isPending ? 'Enregistrement...' : task ? 'Modifier' : 'Creer'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function TaskItem({
  task,
  projectId,
  onEdit,
}: {
  task: ProjectTask
  projectId: string
  onEdit: () => void
}) {
  const updateTask = useUpdateTask()
  const deleteTask = useDeleteTask()

  const isOverdue =
    task.due_date &&
    new Date(task.due_date) < new Date() &&
    task.status !== 'done'

  const provenance = getProvenanceFromNotes(task.notes)

  const handleStatusToggle = async () => {
    const newStatus =
      task.status === 'done' ? 'todo' : task.status === 'todo' ? 'in_progress' : 'done'
    await updateTask.mutateAsync({
      taskId: task.id,
      projectId,
      data: { status: newStatus },
    })
  }

  const handleDelete = async () => {
    if (confirm('Voulez-vous vraiment supprimer cette tache ?')) {
      await deleteTask.mutateAsync({ taskId: task.id, projectId })
    }
  }

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border transition-colors ${
        task.status === 'done'
          ? 'bg-beige-50 border-beige-200 opacity-75'
          : 'bg-white border-beige-200 hover:border-beige-300'
      }`}
    >
      <button
        type="button"
        className="mt-0.5 flex-shrink-0"
        onClick={handleStatusToggle}
      >
        <TaskStatusIcon status={task.status} />
      </button>

      <div className="flex-1 min-w-0" onClick={onEdit}>
        <div className="flex items-start justify-between gap-2 cursor-pointer">
          <div>
            <h4
              className={`font-medium ${
                task.status === 'done'
                  ? 'text-brown-500 line-through'
                  : 'text-brown-900'
              }`}
            >
              {task.titre}
            </h4>
            {task.description && (
              <p className="text-sm text-brown-500 mt-0.5 line-clamp-2">
                {task.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {provenance && (
              <Badge variant="blue" size="sm">
                <ArrowRight className="w-3 h-3 mr-1" />
                Intervention
              </Badge>
            )}
            <PriorityBadge priority={task.priority} />
          </div>
        </div>

        {/* Progress bar */}
        {task.progress > 0 && task.status !== 'done' && (
          <div className="flex items-center gap-2 mt-2">
            <div className="flex-1 bg-beige-200 rounded-full h-1.5">
              <div
                className="bg-mint-500 h-1.5 rounded-full transition-all"
                style={{ width: `${task.progress}%` }}
              />
            </div>
            <span className="text-xs text-brown-500 flex-shrink-0">{task.progress}%</span>
          </div>
        )}

        <div className="flex items-center gap-4 mt-2 text-xs text-brown-500">
          {task.start_date && (
            <div className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>
                Debut: {formatDate(task.start_date, { day: 'numeric', month: 'short' })}
              </span>
            </div>
          )}
          {(task.due_date || task.end_date) && (
            <div
              className={`flex items-center gap-1 ${
                isOverdue ? 'text-red-600' : ''
              }`}
            >
              <Calendar className="w-3.5 h-3.5" />
              <span>
                Fin: {formatDate(task.due_date || task.end_date!, { day: 'numeric', month: 'short' })}
              </span>
            </div>
          )}
          {task.notes && !provenance && (
            <div className="flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" />
              <span>Notes</span>
            </div>
          )}
          {task.assignee && (
            <div className="flex items-center gap-1">
              <Avatar
                src={task.assignee.avatar_url}
                name={task.assignee.full_name}
                size="xs"
              />
              <span>{task.assignee.full_name}</span>
            </div>
          )}
        </div>
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={handleDelete}
        className="flex-shrink-0"
      >
        <Trash2 className="w-4 h-4 text-red-500" />
      </Button>
    </div>
  )
}

export function TasksTab({ projectId }: TasksTabProps) {
  const [showModal, setShowModal] = useState(false)
  const [editingTask, setEditingTask] = useState<ProjectTask | undefined>()
  const [statusFilter, setStatusFilter] = useState<ProjectTask['status'] | ''>('')

  const { data: tasks, isLoading } = useProjectTasks(projectId)

  const filteredTasks = statusFilter
    ? tasks?.filter((t) => t.status === statusFilter)
    : tasks

  const todoTasks = tasks?.filter((t) => t.status === 'todo') || []
  const inProgressTasks = tasks?.filter((t) => t.status === 'in_progress') || []
  const doneTasks = tasks?.filter((t) => t.status === 'done') || []

  const handleEdit = (task: ProjectTask) => {
    setEditingTask(task)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setEditingTask(undefined)
    setShowModal(false)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ProjectTask['status'] | '')}
            className="w-40"
          >
            <option value="">Toutes les taches</option>
            {Object.entries(TASK_STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>
                {config.label}
              </option>
            ))}
          </Select>
          <span className="text-sm text-brown-500">
            {filteredTasks?.length || 0} tache{(filteredTasks?.length || 0) > 1 ? 's' : ''}
          </span>
        </div>
        <Button
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setShowModal(true)}
        >
          Nouvelle tache
        </Button>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-beige-100 rounded-full">
          <Circle className="w-4 h-4 text-brown-400" />
          <span className="text-sm font-medium text-brown-700">{todoTasks.length}</span>
          <span className="text-xs text-brown-500">a faire</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-full">
          <Clock className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium text-blue-700">{inProgressTasks.length}</span>
          <span className="text-xs text-blue-500">en cours</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-mint-50 rounded-full">
          <CheckCircle className="w-4 h-4 text-mint-500" />
          <span className="text-sm font-medium text-mint-700">{doneTasks.length}</span>
          <span className="text-xs text-mint-500">terminees</span>
        </div>
      </div>

      {/* Tasks list */}
      {!filteredTasks || filteredTasks.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={<ListTodo className="w-12 h-12" />}
            title="Aucune tache"
            description={
              statusFilter
                ? 'Aucune tache avec ce statut'
                : 'Ajoutez des taches a ce projet'
            }
            action={
              !statusFilter && (
                <Button
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => setShowModal(true)}
                >
                  Creer une tache
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredTasks.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              projectId={projectId}
              onEdit={() => handleEdit(task)}
            />
          ))}
        </div>
      )}

      {/* Task modal */}
      <TaskModal
        key={editingTask?.id ?? 'new'}
        isOpen={showModal}
        onClose={handleCloseModal}
        projectId={projectId}
        task={editingTask}
      />
    </div>
  )
}

export default TasksTab
